import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, BadgeCheck, FileText } from "lucide-react";
import { motion } from "framer-motion";
import {
  FaShieldAlt, // For Zero Depreciation
  FaTools, // For Engine Protection
  FaFileInvoiceDollar, // For RTI
  FaTruckPickup, // For Roadside Assistance
  FaOilCan, // For Consumables Cover
  FaCloudSunRain,
  FaUserLock,
  FaCarCrash,
  FaCarSide,
} from "react-icons/fa";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${
        id === open ? "rotate-180" : ""
      } h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const HondaCarInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Is zero depreciation cover useful for Honda City or CR-V?",
      answer:
        "Yes, particularly during the first few years to reduce claim-related expenses on parts.",
    },
    {
      question: "Are diesel Honda variants costlier to insure?",
      answer:
        "Yes, diesel variants typically attract higher premiums due to higher engine value.",
    },
    {
      question: "Is RTI cover worth it for Honda Jazz or Amaze?",
      answer:
        "It can be valuable for newer owners, especially in case of total loss or theft.",
    },
    {
      question: "Can I purchase Honda insurance online?",
      answer:
        "Yes, most insurers and aggregators offer quick and simple online purchase and renewal.",
    },
    {
      question: "Which Honda model has the lowest premium?",
      answer:
        "Honda Amaze generally has the lowest insurance cost due to its lower IDV and simpler design.",
    },
    {
      question: "Can I transfer NCB when switching Honda cars?",
      answer:
        "Yes, No Claim Bonus is transferable as long as the vehicle is registered in your name.",
    },
    {
      question: "Do insurers cover accessories like infotainment units?",
      answer:
        "Yes, declared accessories can be covered if included at the time of policy purchase.",
    },
    {
      question:
        "Are natural calamity damages covered under comprehensive plans?",
      answer:
        "Yes, flood, storm, fire and theft are covered under standard comprehensive policies.",
    },
    {
      question: "Can I add multiple drivers to my Honda policy?",
      answer:
        "Yes, most insurers allow named drivers like family members under the same policy.",
    },
    {
      question: "How long does a Honda insurance claim take to settle?",
      answer:
        "Typically around seven to fourteen working days, subject to documentation and damage verification.",
    },
    {
      question: "Does car colour affect premium for Honda cars?",
      answer: "No, the exterior colour does not impact insurance premium.",
    },
    {
      question: "Can I switch insurers mid-policy?",
      answer: "Not usually. Switching is generally permitted only at renewal.",
    },
    {
      question: "Is engine protection cover necessary for Honda variants?",
      answer:
        "It is recommended for models driven in flood-prone areas or for diesel vehicles.",
    },
    {
      question: "Is comprehensive insurance available for older Honda cars?",
      answer:
        "Yes, comprehensive or at least third-party coverage can be obtained depending on vehicle condition and insurer policy.",
    },
    {
      question: "What happens if I sell my Honda car before policy expiry?",
      answer:
        "You can either transfer the policy to the buyer or cancel it and receive a pro-rated premium refund.",
    },
    {
      question: "Is insurance more expensive for turbo variants?",
      answer:
        "Yes, slightly higher premiums may apply for higher-powered or turbocharged variants.",
    },
    {
      question: "Are roadside assistance services standard or optional?",
      answer: "Roadside assistance is typically offered as an optional add-on.",
    },
    {
      question: "Can I insure a modified Honda car?",
      answer:
        "Minor modifications are acceptable if declared. Major modifications may require special approval.",
    },
    {
      question: "What is the penalty for driving without insurance?",
      answer:
        "As per Indian law, fines can range from ₹2,000 to ₹4,000, with possible additional legal action.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation",
      description: "Helps avoid deductions on plastic or composite panels.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Engine and Gearbox Protection",
      description:
        "Recommended if you drive in flood-prone or rough conditions.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Return to Invoice (RTI)",
      description: "Useful for newer City, CR-V or Jazz owners.",
      icon: <FaFileInvoiceDollar className="text-4xl text-orange-600 mb-4" />,
    },
    {
      title: "Roadside Assistance",
      description: "Ideal for breakdowns, towing and flat tyre support.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Consumables Cover",
      description: "Covers oils, filters, bolts and minor parts during repair.",
      icon: <FaOilCan className="text-4xl text-green-600 mb-4" />,
    },
  ];

  const features = [
    { icon: "🛡️", text: "Reliable & Accurate Estimates" },
    { icon: "⏱️", text: "Takes Less Than a Minute" },
    { icon: "📉", text: "Instantly Shows Premium Breakup" },
    { icon: "📄", text: "No Paperwork or Documentation" },
    { icon: "📲", text: "Easy to Use on Any Device" },
    { icon: "💰", text: "Helps You Save on Insurance" },
  ];
 const hondainsurance = [
    {
      name: "City",
      path: "/honda-city-car-insurance",
      image: "/assets/hondacity.jpeg",
    },
{
      name: "Amaze",
      path: "/honda-amaze-car-insurance",
      image: "/assets/hondaamaze.jpeg",
    },
    {
      name: "Elevate",
      path: "/honda-elevate-car-insurance",
      image: "/assets/hondaelevate.jpeg",
    },
     {
      name: "WRV",
      path: "/honda-wrv-car-insurance",
      image: "/assets/hondaWRV.jpeg",
    },
     {
      name: "Jazz",
      path: "/honda-jazz-car-insurance",
      image: "/assets/hondajazz.jpeg",
    },
     {
      name: "City Hybrid",
      path: "/honda-city-hybrid-car-insurance",
      image: "/assets/hondacityhybrid.jpeg",
    },
     {
      name: "Accord Hybrid",
      path: "/honda-accord-hybrid-car-insurance",
      image: "/assets/hondaaccordhybrid.jpeg",
    },
     {
      name: "CRV",
      path: "/honda-crv-car-insurance",
      image: "/assets/hondacrv.jpeg",
    },
  ];

  return (
    <>
      <Helmet>
        <title>
          Honda Car Insurance in India – Compare Quotes & Buy Online
        </title>
        <meta
          name="description"
          content="Explore Honda car insurance options in India. Compare quotes, customize coverage, and buy online to protect your Honda vehicle."
        />
        <meta
          name="keywords"
          content="honda car insurance, auto insurance india, honda city insurance, honda amaze insurance, car insurance quotes"
        />
          <link
          rel="canonical"
          href="https://thefinmaster.com/honda-car-insurance"
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
        
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">Honda Car Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">
                    Honda Car Insurance 
                  </span>{" "}
                  
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                Honda is renowned for its refined engineering, reliability, and smooth driving experience. Whether you own a compact Amaze or a stylish City, protecting your vehicle starts with choosing the right insurance. This guide walks you through everything you need to know about insuring a Honda car, including average premiums, model-specific insights, useful add-ons, renewal tips, and frequently asked questions.

                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/hondainsurance.jpeg"
                alt="Honda Car"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center p-4 mt-6">
        <div className="bg-white shadow-lg rounded-2xl p-6 max-w-7xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            About Honda in India
          </h2>
          <p className="text-gray-600 leading-relaxed">
            Honda Cars India Ltd has earned customer trust with dependable build
            quality, fuel-efficient engines and strong after-sales service. Its
            Indian lineup includes hatchbacks, sedans, compact SUVs and premium
            SUVs. Popular models such as Amaze, City, Jazz, WR-V and CR-V are
            recognized for their comfort, efficiency and low running costs.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            Pairing your Honda with the right car insurance is as important as
            maintenance for long-term ownership satisfaction.
          </p>
        </div>
      </div>
        <div className="p-4 sm:p-8 bg-white">
                    <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
                      Choose Your Car Brand
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                      {hondainsurance.map((calc, index) => (
                        <motion.div
                          key={index}
                          whileHover={{ scale: 1.04 }}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                        >
                          <Link
                            to={calc.path}
                            className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
                          >
                            <img
                              src={calc.image}
                              alt={calc.name}
                              className="w-16 h-16 object-contain mb-2"
                            />
                            <span className="text-md font-medium text-gray-800 break-words leading-snug">
                              {calc.name}
                            </span>
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                  </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why Honda Owners Need a Good Insurance Plan
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            Honda vehicles often feature advanced safety systems, efficient
            engines and well-appointed interiors. Repair or replacement costs
            can add up, especially for premium models. A solid insurance policy
            helps protect you from:
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>Damage caused by accidents or collisions</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>Theft, fire or vandalism</span>
            </li>
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>Natural disasters including floods and storms</span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>Third-party liability claims</span>
            </li>
            <li className="flex items-start gap-3">
              <FaTools className="text-xl text-indigo-500 mt-1" />
              <span>Rising costs for parts and electronics</span>
            </li>
          </ul>
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            Your insurance policy also gives access to Honda-authorized network
            garages, cashless repairs and add-on flexibility tailored to your
            driving needs.
          </div>
        </motion.div>
      </section>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your Car Insurance <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your premium — no
              paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/car-insurance-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl" role="img" aria-label={item.text}>
                  {item.icon}
                </span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Popular Honda Models and Insurance Cost Estimates
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Here’s an overview of Honda’s core models in India along with
          approximate Insured Declared Value (IDV) and starting annual premiums
          for comprehensive insurance:
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              {
                model: "Honda Amaze",
                category: "Compact Sedan",
                idv: "6–9 Lakh",
                premium: "₹3,200 to ₹5,000",
              },
              {
                model: "Honda City",
                category: "Mid-Size Sedan",
                idv: "10–15 Lakh",
                premium: "₹4,200 to ₹7,500",
              },
              {
                model: "Honda Jazz",
                category: "Premium Hatchback",
                idv: "8–12 Lakh",
                premium: "₹3,800 to ₹5,800",
              },
              {
                model: "Honda WR-V",
                category: "Compact SUV",
                idv: "8–11 Lakh",
                premium: "₹4,000 to ₹6,200",
              },
              {
                model: "Honda CR-V (Diesel)",
                category: "Premium SUV",
                idv: "24–30 Lakh",
                premium: "₹7,500 to ₹11,000",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="border mb-4 p-4 rounded-lg bg-white shadow-md"
              >
                <h3 className="font-semibold text-gray-800 mb-2">
                  {item.model}
                </h3>
                <div className="text-gray-600 mb-1">
                  <strong>Category:</strong> {item.category}
                </div>
                <div className="text-gray-600 mb-1">
                  <strong>Approx. IDV:</strong> {item.idv}
                </div>
                <div className="text-gray-600">
                  <strong>Starting Premium:</strong> {item.premium}
                </div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">
                    Model
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Category
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Approx. IDV (₹)
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Starting Premium (₹)
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  [
                    "Honda Amaze",
                    "Compact Sedan",
                    "6–9 Lakh",
                    "₹3,200 to ₹5,000",
                  ],
                  [
                    "Honda City",
                    "Mid-Size Sedan",
                    "10–15 Lakh",
                    "₹4,200 to ₹7,500",
                  ],
                  [
                    "Honda Jazz",
                    "Premium Hatchback",
                    "8–12 Lakh",
                    "₹3,800 to ₹5,800",
                  ],
                  [
                    "Honda WR-V",
                    "Compact SUV",
                    "8–11 Lakh",
                    "₹4,000 to ₹6,200",
                  ],
                  [
                    "Honda CR-V (Diesel)",
                    "Premium SUV",
                    "24–30 Lakh",
                    "₹7,500 to ₹11,000",
                  ],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Add-Ons That Make Sense for Honda Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Honda vehicles come with modern features and efficient engine setups.
          The following add-on covers can enhance protection and save on repair
          costs:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Best Insurance Companies for Honda Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Several insurers offer strong policies for Honda owners with good
          claim support and garage networks. Top choices include:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              HDFC ERGO
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Popular for its digital service and fast claim processing.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              ICICI Lombard
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Excellent for premium models like CR-V and City.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Tata AIG
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Flexible plans with add-ons suitable for Honda variants.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📋</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Reliance General
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Known for competitive pricing and streamlined issuance.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Bajaj Allianz
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Trusted for claim settlement and garage tie-ups.
            </p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                How to Save on Honda Car Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Here are practical steps to reduce your premium while keeping
              solid coverage intact:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Compare quotes from different insurers before buying or
                  renewing.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Maintain a claim-free history to avail No Claim Bonus (NCB).
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Opt for voluntary deductibles if you are a cautious driver.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Install ARAI-approved anti-theft devices to earn discounts.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Renew your policy before expiry to avoid penalties or
                  inspection demands.
                </span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for Honda Car Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              To purchase or renew insurance for your Honda, keep the following
              documents ready:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Vehicle Registration Certificate (RC)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Existing policy documents, if applicable</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Valid Pollution Under Control (PUC) certificate</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Identity proof such as Aadhaar or PAN</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl sm:col-span-2">
                <span>
                  Passport-size photo (required only for some offline purchases)
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          Honda Car Insurance FAQs
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <FaCarSide />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Honda cars offer a balanced combination of reliability, modern
                features and refined driving experience. With a well-chosen
                insurance policy, you can drive confidently and avoid financial
                surprises. Whether you trust the City for daily commutes or the
                CR-V for longer drives, a carefully structured policy ensures
                smoother ownership.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                At TheFinMaster, we help you compare covers, select the best
                add-ons and get competitive premiums tailored to your Honda
                vehicle.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default HondaCarInsurance;
